import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getGoogleSearchSuggestions } from './searchProviders';
import {
  trackRefineSearch,
  trackSearch,
  trackSearchInteraction,
  trackSuggestionSearch,
} from './tracking';
import { buildSearchListingsPagePath } from './utils/buildSearchPath';
import { getSuggestionByGlobalIndex } from './utils/getSuggestionByGlobalIndex';
import type { SuggestionsGroup } from 'client-site-chrome-ui/components/molecules/SearchSuggestions/types';
import type { Environment } from 'client-site-chrome-ui/environment';
import { useCookieValue } from 'client-site-chrome-ui/hooks/useCookieValue';
import type { Cookies } from 'client-site-chrome-ui/types/cookies';

interface UseSearchParams {
  cookies?: Cookies;
  env?: Environment;
  searchedTerm?: string;
}

const MIN_CHARS_FOR_SUGGESTIONS = 2;

export const useSearch = ({
  cookies,
  env = 'development',
  searchedTerm: initialTerm,
}: UseSearchParams) => {
  const { value: gaUserId } = useCookieValue({ cookies, cookieName: '_ga' });
  const [searchTerm, setSearchTerm] = useState<string>(initialTerm || '');
  const [isSearchInFocus, setSearchFocus] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestionsGroup[]>([]);
  const [isSuggestionsShown, setIsSuggestionsShown] = useState(false);
  const [selectedSuggestionId, setSelectedSuggestionId] = useState(-1);
  const [suggestionsCount, setSuggestionsCount] = useState(0);
  const isSearchInteractionEventSent = useRef(false);
  const [searchTermHasBeenReset, setSearchTermHasBeenReset] = useState(false);
  const fakeGaUserId = useRef<string>(String(Date.now()));

  const resetSelectedSuggestion = useCallback(() => setSelectedSuggestionId(-1), []);

  const resetSuggestions = useCallback(() => setSuggestions([]), []);

  const resetSearchTerm = useCallback(() => {
    setSearchTerm('');
    setSearchTermHasBeenReset(true);
  }, []);

  const hideSuggestions = useCallback(() => {
    setIsSuggestionsShown(false);
  }, []);

  const selectPreviousSearchSuggestion = useCallback(() => {
    if (isSuggestionsShown) {
      setSelectedSuggestionId(Math.max(selectedSuggestionId - 1, -1));
    }
  }, [isSuggestionsShown, selectedSuggestionId]);

  const selectNextSearchSuggestion = useCallback(() => {
    if (isSuggestionsShown) {
      setSelectedSuggestionId(Math.min(selectedSuggestionId + 1, suggestionsCount - 1));
    }
  }, [isSuggestionsShown, selectedSuggestionId, suggestionsCount]);

  const handleSearchInteracted = () => {
    if (!isSearchInteractionEventSent.current) {
      isSearchInteractionEventSent.current = true;
      trackSearchInteraction();
    }
  };

  const updateSuggestions = async () => {
    const { groups = null, suggestionsCount = null } =
      (await getGoogleSearchSuggestions(env, searchTerm, gaUserId || fakeGaUserId.current)) || {};

    if (suggestionsCount && groups) {
      setSuggestions(groups);
      setIsSuggestionsShown(groups.length > 0);
      setSuggestionsCount(suggestionsCount);
    } else {
      resetSuggestions();
    }
  };

  const handleTermChange = useCallback(async () => {
    const termEntered =
      searchTerm.length > 0
        ? searchTerm.length > MIN_CHARS_FOR_SUGGESTIONS
          ? 'FULL'
          : 'PARTIAL'
        : 'NONE';

    switch (termEntered) {
      case 'FULL':
        handleSearchInteracted();
        updateSuggestions();
        break;
      case 'PARTIAL':
        resetSuggestions();
        break;
      case 'NONE':
      default:
        resetAll();
        break;
    }
  }, [searchTerm, suggestions.length, env]);

  const handleSearch = useCallback(async () => {
    const term = searchTerm.trim();
    const isRefinedSearch = initialTerm && term !== initialTerm && !searchTermHasBeenReset;
    const isSuggestionSearch = selectedSuggestionId !== -1;

    if (isSuggestionSearch) {
      const suggestion = getSuggestionByGlobalIndex(suggestions, selectedSuggestionId);

      if (suggestion) {
        await trackSuggestionSearch(term, suggestion);
        window.location.assign(suggestion.href);
      }
    } else {
      await Promise.all([
        isRefinedSearch && trackRefineSearch(initialTerm, term),
        trackSearch(term),
      ]);

      window.location.assign(buildSearchListingsPagePath(term));
    }
  }, [searchTerm, selectedSuggestionId, suggestions]);

  const resetAll = useCallback(() => {
    resetSearchTerm();
    resetSelectedSuggestion();
    resetSuggestions();
  }, [resetSelectedSuggestion, resetSuggestions]);

  useEffect(() => {
    handleTermChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    setIsSuggestionsShown(suggestions.length > 0 && isSearchInFocus);
  }, [suggestions.length, isSearchInFocus]);

  const selectedSuggestionTerm =
    getSuggestionByGlobalIndex(suggestions, selectedSuggestionId)?.name || '';

  const searchData = useMemo(
    () => ({
      resetAll,
      searchTerm,
      setSearchTerm,
      handleSearch,
      suggestions,
      isSearchInFocus,
      isSuggestionsShown,
      setSearchFocus,
      hideSuggestions,
      suggestionsCount,
      selectedSuggestionId,
      selectNextSearchSuggestion,
      selectPreviousSearchSuggestion,
      resetSelectedSuggestion,
      selectedSuggestionTerm,
    }),
    [
      handleSearch,
      hideSuggestions,
      isSearchInFocus,
      isSuggestionsShown,
      resetAll,
      resetSelectedSuggestion,
      searchTerm,
      selectNextSearchSuggestion,
      selectPreviousSearchSuggestion,
      selectedSuggestionId,
      selectedSuggestionTerm,
      suggestions,
      suggestionsCount,
    ],
  );

  return searchData;
};
